import { Button, Modal, Typography, Table, Tag } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useFetchMeQuery } from "features/auth/authAPI";
import {
  useDismissDebtorReminderMutation,
  useFetchDebtorRemindersQuery,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";

import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { DATE_TIME_FORMAT } from "common/constants";
import { useFetchUserSummariesQuery } from "features/userManagementTable/userManagementAPI";

const { Text } = Typography;

const StyledButton = styled(Button)`
  padding-left: 0;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledWarningIcon = styled.span`
  color: #faad14;
  font-size: 16px;
`;

function RemindersProvider({ children, userType }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hasShownReminders, setHasShownReminders] = useState(false);
  const [clickedButtons, setClickedButtons] = useState(new Set());
  const navigate = useNavigate();
  const [dismissReminder, { isLoading: isDismissingReminder }] = useDismissDebtorReminderMutation();
  const { data: me } = useFetchMeQuery();

  const { data: reminders = [] } = useFetchDebtorRemindersQuery(
    {
      assigneeIds: [me?.id],
      isActive: true,
    },
    {
      skip: userType !== "agency",
    },
  );
  // skip users query for userType !== "agency"
  const { data: users, isLoading: isUsersLoading } = useFetchUserSummariesQuery(
    {
      isActive: true,
    },
    { skip: userType !== "agency" },
  );

  // Show modal when there are reminders and user hasn't seen them yet
  useEffect(() => {
    if (reminders.length > 0 && !isModalVisible && !hasShownReminders) {
      setIsModalVisible(true);
    }
  }, [isModalVisible, reminders.length, hasShownReminders]);

  // Only render children for agency users
  if (userType !== "agency") {
    return children;
  }

  const handleDismiss = async (reminderId) => {
    // Mark button as clicked
    setClickedButtons((prev) => new Set([...prev, reminderId]));

    await dismissReminder({
      userTasks: [
        {
          id: reminderId,
          status: "completed",
        },
      ],
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setHasShownReminders(true);
  };

  const columns = [
    {
      title: "Debtor",
      dataIndex: "assigneeId",
      key: "assigneeId",
      render: (text, record) => (
        <div>
          <StyledButton
            type="link"
            onClick={() => navigate(`/debtors/${record.debtor?.id}`)}
            target="_blank"
          >
            {record.debtor?.fullName}
          </StyledButton>{" "}
          <div>(ID: {record.debtor?.id ?? "Unknown"})</div>
        </div>
      ),
    },
    {
      title: "Reminder Date",
      dataIndex: "remindAt",
      key: "remindAt",
      render: (text, record) => moment(record.remindAt).format(DATE_TIME_FORMAT),
    },
    {
      title: "Reminder",
      dataIndex: "message",
      key: "message",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Created By",
      dataIndex: "createdById",
      key: "createdById",
      render: (text, record) => users?.find((user) => user.id === record.createdById)?.name || "-",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => moment(record.createdAt).format(DATE_TIME_FORMAT),
    },
    {
      title: "Actions",
      fixed: "right",
      key: "action",
      render: (_, record) => {
        const isClicked = clickedButtons.has(record.id);

        return (
          <div>
            {isClicked ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                Completed
              </Tag>
            ) : (
              <Button
                danger
                type="primary"
                onClick={() => handleDismiss(record.id)}
                loading={isDismissingReminder}
                disabled={isDismissingReminder}
              >
                Dismiss
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      {children}
      <Modal
        title={
          <StyledTitleContainer>
            <StyledWarningIcon>⚠️</StyledWarningIcon>
            New Reminders
          </StyledTitleContainer>
        }
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={1100}
        closable
        maskClosable={false}
      >
        <Table
          dataSource={reminders}
          // @ts-ignore
          columns={columns}
          pagination={false}
          rowKey="id"
          size="small"
        />
      </Modal>
    </>
  );
}

export default RemindersProvider;
